import {
    Box,
    Button,
    Divider,
    Skeleton,
    Stack,
    styled,
    useTheme
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useTranslation } from 'react-i18next';

import { BankIcon } from '@/icons/BankIcon';
import { Caption, LINE_HEIGHT, SMBold } from '@/typography/Typography';
import { HouseAccountBankConnectionStatus } from '@/services/PaymentsServiceV2';
import { useGetHouseAccountConnectionStatus } from '@/services/paymentsQuery';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useErrorHandling } from '@/utils/errorHandling';

interface BankConnectionStatusProps {
    instaPaidHouseAccountId: string;
}

const StatusContainer = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: '8px',
    border: `1px solid ${theme.palette.greyBorder}`,
    backgroundColor: theme.palette.background.paper
}));

const IconSquare = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '32px',
    width: '32px',
    borderRadius: '6px',
    backgroundColor: theme.palette.successBackground
}));

// Colour is supplied via inline style so it can stay a per-status theme token
// (not a hardcoded hex); the styled base only owns the fixed shape.
const StatusDot = styled('span')({
    height: '8px',
    width: '8px',
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0
});

const InlineAction = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    minWidth: 'auto',
    padding: theme.spacing(0, 0.5),
    color: theme.palette.text.success,
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: LINE_HEIGHT.SMALL,
    '&:hover': { backgroundColor: 'transparent' }
}));

/**
 * Bank-connection status box + connect/re-authorize action for a single house
 * account. Rendered only once an account is selected. The dot colour comes from
 * theme tokens, and clicking the action starts the Aiia flow by redirecting the
 * browser to the server-issued connect URL.
 */
export const BankConnectionStatus = ({
    instaPaidHouseAccountId
}: BankConnectionStatusProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const { data, isLoading, isError, refetch } =
        useGetHouseAccountConnectionStatus(instaPaidHouseAccountId);
    const { connectHouseAccountAiia } = usePaymentsMutations();
    const { backendErrorHandler } = useErrorHandling();

    const bankIcon = (
        <IconSquare>
            <BankIcon sx={{ fontSize: '18px' }} />
        </IconSquare>
    );

    if (isLoading) {
        return (
            <StatusContainer data-testid="bank-connection-loading">
                <Skeleton variant="rounded" width={32} height={32} />
                <Stack direction="column" gap={0.5}>
                    <Skeleton variant="text" width={90} height={12} />
                    <Skeleton variant="text" width={70} height={16} />
                </Stack>
            </StatusContainer>
        );
    }

    // An endpoint failure must not silently remove the box (and with it the only
    // way to connect): show the failure and a retry instead.
    if (isError) {
        return (
            <StatusContainer data-testid="bank-connection-error">
                {bankIcon}
                <Stack direction="column">
                    <Caption>
                        {t('transaction_monitoring.bank_connection_caption')}
                    </Caption>
                    <SMBold color={theme.palette.text.error}>
                        {t('transaction_monitoring.connection_status_error')}
                    </SMBold>
                </Stack>
                <Divider orientation="vertical" flexItem />
                <InlineAction
                    onClick={() => refetch()}
                    startIcon={<AutorenewIcon sx={{ fontSize: '16px' }} />}
                >
                    {t('transaction_monitoring.connection_status_retry')}
                </InlineAction>
            </StatusContainer>
        );
    }

    const status = data?.data?.connectionStatus;
    if (!status) {
        return null;
    }

    const dotColorByStatus: Record<HouseAccountBankConnectionStatus, string> = {
        Active: theme.palette.text.success,
        Lapsed: theme.palette.warning.main,
        NotConnected: theme.palette.text.muted
    };

    const statusLabelByStatus: Record<
        HouseAccountBankConnectionStatus,
        string
    > = {
        Active: t('transaction_monitoring.connection_active'),
        Lapsed: t('transaction_monitoring.connection_lapsed'),
        NotConnected: t('transaction_monitoring.connection_not_connected')
    };

    // "Connect" for a never-authorized account; "Re-authorize" once a binding
    // exists (Active or Lapsed).
    const actionLabel =
        status === 'NotConnected'
            ? t('transaction_monitoring.connect')
            : t('transaction_monitoring.reauthorize');

    const handleConnect = () => {
        connectHouseAccountAiia.mutate(instaPaidHouseAccountId, {
            onSuccess: (response) => {
                window.location.href = response.data.connectUrl;
            },
            onError: (mutationError) =>
                backendErrorHandler(t('transaction_monitoring.connect_error'))(
                    mutationError
                )
        });
    };

    return (
        <StatusContainer data-testid="bank-connection-status">
            {bankIcon}
            <Stack direction="column">
                <Caption>
                    {t('transaction_monitoring.bank_connection_caption')}
                </Caption>
                <Stack direction="row" alignItems="center" gap={'6px'}>
                    <StatusDot
                        data-testid="bank-connection-dot"
                        style={{ backgroundColor: dotColorByStatus[status] }}
                    />
                    <SMBold>{statusLabelByStatus[status]}</SMBold>
                </Stack>
            </Stack>
            <Divider orientation="vertical" flexItem />
            <InlineAction
                onClick={handleConnect}
                disabled={connectHouseAccountAiia.isPending}
                startIcon={<AutorenewIcon sx={{ fontSize: '16px' }} />}
            >
                {actionLabel}
            </InlineAction>
        </StatusContainer>
    );
};

export default BankConnectionStatus;
